import { z } from 'zod';
import { mailTagRuleSchema, type MailTagRule, type CreateMailTagRule } from '@mrrmlab/shared-types';
export type { MailTagRule, CreateMailTagRule };
import type { HttpClient } from './http';

export const mailTagSchema = z.object({
  id: z.string().uuid(),
  ownerSub: z.string(),
  name: z.string(),
  createdAt: z.string(),
  messageCount: z.number().optional(),
});
export type MailTag = z.infer<typeof mailTagSchema>;

export const filteredMessageTagSchema = z.object({
  tagId: z.string().uuid(),
  name: z.string(),
  status: z.enum(['suggested', 'confirmed']),
});
export type FilteredMessageTag = z.infer<typeof filteredMessageTagSchema>;

export const filteredMessageSchema = z.object({
  id: z.string().uuid(),
  uid: z.number(),
  subject: z.string().nullable(),
  fromAddr: z.string().nullable(),
  date: z.string().nullable(),
  flags: z.array(z.string()),
  tags: z.array(filteredMessageTagSchema),
});
export type FilteredMessage = z.infer<typeof filteredMessageSchema>;

export const filteredMessagesPageSchema = z.object({
  messages: z.array(filteredMessageSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type FilteredMessagesPage = z.infer<typeof filteredMessagesPageSchema>;

export const mailMessageTagSchema = z.object({
  id: z.string().uuid(),
  messageId: z.string().uuid(),
  tagId: z.string().uuid(),
  status: z.enum(['suggested', 'confirmed']),
  createdAt: z.string(),
  tag: mailTagSchema.optional(),
});
export type MailMessageTag = z.infer<typeof mailMessageTagSchema>;

export const mailAccountSchema = z.object({
  id: z.string().uuid(),
  ownerSub: z.string(),
  label: z.string(),
  host: z.string(),
  port: z.number(),
  tls: z.boolean(),
  username: z.string(),
  trackingScanEnabled: z.boolean(),
  lastScanAt: z.string().nullable(),
  createdAt: z.string(),
});
export type MailAccount = z.infer<typeof mailAccountSchema>;

export const updateMailAccountSchema = z.object({
  label: z.string().min(1).max(100).optional(),
  trackingScanEnabled: z.boolean().optional(),
});
export type UpdateMailAccountDto = z.infer<typeof updateMailAccountSchema>;

export const createMailAccountSchema = z.object({
  label: z.string(),
  host: z.string(),
  port: z.number(),
  tls: z.boolean(),
  username: z.string(),
  password: z.string(),
});
export type CreateMailAccountDto = z.infer<typeof createMailAccountSchema>;

export const folderSchema = z.object({
  name: z.string(),
  delimiter: z.string().nullable(),
  flags: z.array(z.string()),
});
export type MailFolder = z.infer<typeof folderSchema>;

export const messageEnvelopeSchema = z.object({
  uid: z.number(),
  subject: z.string().nullable(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  date: z.string().nullable(),
  flags: z.array(z.string()),
  archived: z.boolean().default(false),
});
export type MessageEnvelope = z.infer<typeof messageEnvelopeSchema>;

export const archiveMailResultSchema = z.object({
  paperlessDocumentId: z.number(),
  archivedAt: z.string(),
  alreadyArchived: z.boolean(),
});
export type ArchiveMailResult = z.infer<typeof archiveMailResultSchema>;

export const messagesPageSchema = z.object({
  messages: z.array(messageEnvelopeSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type MessagesPage = z.infer<typeof messagesPageSchema>;

export const fullMessageSchema = messageEnvelopeSchema.extend({
  id: z.string().uuid(),
  bodyText: z.string().nullable(),
  bodyHtml: z.string().nullable(),
});
export type FullMessage = z.infer<typeof fullMessageSchema>;

export const reviewQueueTagSchema = z.object({
  id: z.string().uuid(),
  tagId: z.string().uuid(),
  name: z.string(),
  status: z.enum(['suggested', 'confirmed']),
});
export type ReviewQueueTag = z.infer<typeof reviewQueueTagSchema>;

export const reviewQueueItemSchema = z.object({
  messageId: z.string().uuid(),
  subject: z.string().nullable(),
  fromAddr: z.string().nullable(),
  snippet: z.string().nullable(),
  tags: z.array(reviewQueueTagSchema),
});
export type ReviewQueueItem = z.infer<typeof reviewQueueItemSchema>;

export const reviewQueueSchema = z.object({
  total: z.number(),
  items: z.array(reviewQueueItemSchema),
});
export type ReviewQueue = z.infer<typeof reviewQueueSchema>;

export const connectionTestSchema = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
});

export const senderMemoryTagSchema = z.object({
  senderAddr: z.string(),
  tagId: z.string().uuid(),
  name: z.string(),
  confirmCount: z.number().int().nonnegative(),
  removeCount: z.number().int().nonnegative(),
  matchType: z.enum(['exact', 'domain']).default('exact'),
});
export type SenderMemoryTag = z.infer<typeof senderMemoryTagSchema>;

export const senderMemoryEntrySchema = z.object({
  senderAddr: z.string(),
  tags: z.array(senderMemoryTagSchema),
});
export type SenderMemoryEntry = z.infer<typeof senderMemoryEntrySchema>;

export const senderMemoryLookupResponseSchema = z.array(senderMemoryEntrySchema);

export const senderMemoryListEntrySchema = z.object({
  id: z.string().uuid(),
  senderAddr: z.string(),
  tagId: z.string().uuid(),
  tagName: z.string(),
  confirmCount: z.number().int().nonnegative(),
  removeCount: z.number().int().nonnegative(),
  lastSeenAt: z.coerce.date(),
});
export type SenderMemoryListEntry = z.infer<typeof senderMemoryListEntrySchema>;

export const senderMemoryListPageSchema = z.object({
  entries: z.array(senderMemoryListEntrySchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});
export type SenderMemoryListPage = z.infer<typeof senderMemoryListPageSchema>;

export const nliDebugLogPayloadSchema = z.object({
  messageId: z.string().optional(),
  accountId: z.string().optional(),
  folder: z.string().optional(),
  uid: z.number().int().optional(),
  subject: z.string().nullable(),
  from: z.string().nullable(),
  date: z.string().nullable(),
  snippetRaw: z.string(),
  snippetCleaned: z.string(),
  candidates: z.array(z.object({ label: z.string(), score: z.number() })).min(1),
  templateUsed: z.string(),
  clientTimestamp: z.string().optional(),
});
export type NliDebugLogPayload = z.infer<typeof nliDebugLogPayloadSchema>;

export interface GetMessagesParams {
  page?: number;
  limit?: number;
  since?: string;
}

export interface GetFilteredMessagesParams {
  page?: number;
  limit?: number;
  tagIds?: string[];
  hasSuggested?: boolean;
  untagged?: boolean;
}

export class MailResource {
  constructor(private readonly http: HttpClient) {}

  listAccounts(signal?: AbortSignal): Promise<MailAccount[]> {
    return this.http.request({
      path: '/mail/accounts',
      responseSchema: z.array(mailAccountSchema),
      signal,
    });
  }

  createAccount(dto: CreateMailAccountDto, signal?: AbortSignal): Promise<MailAccount> {
    return this.http.request({
      method: 'POST',
      path: '/mail/accounts',
      body: dto,
      responseSchema: mailAccountSchema,
      signal,
    });
  }

  deleteAccount(id: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/mail/accounts/${encodeURIComponent(id)}`,
      expectEmpty: true,
      signal,
    });
  }

  updateAccount(id: string, dto: UpdateMailAccountDto, signal?: AbortSignal): Promise<MailAccount> {
    return this.http.request({
      method: 'PATCH',
      path: `/mail/accounts/${encodeURIComponent(id)}`,
      body: dto,
      responseSchema: mailAccountSchema,
      signal,
    });
  }

  testConnection(id: string, signal?: AbortSignal) {
    return this.http.request({
      method: 'POST',
      path: `/mail/accounts/${encodeURIComponent(id)}/test`,
      responseSchema: connectionTestSchema,
      signal,
    });
  }

  listFolders(accountId: string, signal?: AbortSignal): Promise<MailFolder[]> {
    return this.http.request({
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders`,
      responseSchema: z.array(folderSchema),
      signal,
    });
  }

  listMessages(accountId: string, folder: string, params: GetMessagesParams = {}, signal?: AbortSignal): Promise<MessagesPage> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.since) query.set('since', params.since);
    const qs = query.toString();
    return this.http.request({
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders/${encodeURIComponent(folder)}/messages${qs ? `?${qs}` : ''}`,
      responseSchema: messagesPageSchema,
      signal,
    });
  }

  getMessage(accountId: string, folder: string, uid: number, signal?: AbortSignal): Promise<FullMessage> {
    return this.http.request({
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders/${encodeURIComponent(folder)}/messages/${uid}`,
      responseSchema: fullMessageSchema,
      signal,
    });
  }

  archiveMessage(
    accountId: string,
    folder: string,
    uid: number,
    signal?: AbortSignal,
  ): Promise<ArchiveMailResult> {
    return this.http.request({
      method: 'POST',
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders/${encodeURIComponent(folder)}/messages/${uid}/archive`,
      responseSchema: archiveMailResultSchema,
      signal,
    });
  }

  listMessageTagsByUids(accountId: string, folder: string, uids: number[], signal?: AbortSignal) {
    const schema = z.array(z.object({ uid: z.number(), tags: z.array(z.object({ name: z.string(), status: z.string() })) }));
    return this.http.request({
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders/${encodeURIComponent(folder)}/message-tags?uids=${uids.join(',')}`,
      responseSchema: schema,
      signal,
    });
  }

  getReviewQueue(limit?: number, signal?: AbortSignal): Promise<ReviewQueue> {
    const qs = limit !== undefined ? `?limit=${limit}` : '';
    return this.http.request({
      path: `/mail/review-queue${qs}`,
      responseSchema: reviewQueueSchema,
      signal,
    });
  }

  listTags(signal?: AbortSignal): Promise<MailTag[]> {
    return this.http.request({ path: '/mail/tags', responseSchema: z.array(mailTagSchema), signal });
  }

  renameTag(tagId: string, name: string, signal?: AbortSignal): Promise<MailTag> {
    return this.http.request({
      method: 'PATCH',
      path: `/mail/tags/${encodeURIComponent(tagId)}`,
      body: { name },
      responseSchema: mailTagSchema,
      signal,
    });
  }

  mergeTag(sourceTagId: string, targetTagId: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'POST',
      path: `/mail/tags/${encodeURIComponent(sourceTagId)}/merge`,
      body: { targetTagId },
      expectEmpty: true,
      signal,
    });
  }

  deleteTag(tagId: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({ method: 'DELETE', path: `/mail/tags/${encodeURIComponent(tagId)}`, expectEmpty: true, signal });
  }

  getFilteredMessages(accountId: string, folder: string, params: GetFilteredMessagesParams = {}, signal?: AbortSignal): Promise<FilteredMessagesPage> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.tagIds?.length) query.set('tagIds', params.tagIds.join(','));
    if (params.hasSuggested) query.set('hasSuggested', 'true');
    if (params.untagged) query.set('untagged', 'true');
    const qs = query.toString();
    return this.http.request({
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders/${encodeURIComponent(folder)}/filtered-messages${qs ? `?${qs}` : ''}`,
      responseSchema: filteredMessagesPageSchema,
      signal,
    });
  }

  listMessageTags(messageId: string, signal?: AbortSignal): Promise<MailMessageTag[]> {
    return this.http.request({
      path: `/mail/messages/${encodeURIComponent(messageId)}/tags`,
      responseSchema: z.array(mailMessageTagSchema),
      signal,
    });
  }

  assignTag(messageId: string, name: string, status: 'suggested' | 'confirmed' = 'suggested', signal?: AbortSignal): Promise<MailMessageTag> {
    return this.http.request({
      method: 'POST',
      path: `/mail/messages/${encodeURIComponent(messageId)}/tags`,
      body: { name, status },
      responseSchema: mailMessageTagSchema,
      signal,
    });
  }

  confirmTag(messageId: string, tagId: string, signal?: AbortSignal): Promise<MailMessageTag> {
    return this.http.request({
      method: 'PATCH',
      path: `/mail/messages/${encodeURIComponent(messageId)}/tags/${encodeURIComponent(tagId)}/confirm`,
      responseSchema: mailMessageTagSchema,
      signal,
    });
  }

  removeMessageTag(messageId: string, tagId: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/mail/messages/${encodeURIComponent(messageId)}/tags/${encodeURIComponent(tagId)}`,
      expectEmpty: true,
      signal,
    });
  }

  replyToMessage(
    accountId: string,
    folder: string,
    uid: number,
    dto: { body: string; to?: string },
    signal?: AbortSignal,
  ): Promise<void> {
    return this.http.request({
      method: 'POST',
      path: `/mail/accounts/${encodeURIComponent(accountId)}/folders/${encodeURIComponent(folder)}/messages/${uid}/reply`,
      body: dto,
      expectEmpty: true,
      signal,
    });
  }

  postNliDebugLog(payload: NliDebugLogPayload, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'POST',
      path: '/mail/_debug/nli-log',
      body: payload,
      expectEmpty: true,
      signal,
    });
  }

  lookupSenderMemory(senderAddrs: string[], signal?: AbortSignal): Promise<SenderMemoryEntry[]> {
    return this.http.request({
      method: 'POST',
      path: '/mail/sender-memory/lookup',
      body: { senderAddrs },
      responseSchema: senderMemoryLookupResponseSchema,
      signal,
    });
  }

  listSenderMemory(
    params: { page?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<SenderMemoryListPage> {
    const search = new URLSearchParams();
    if (params.page !== undefined) search.set('page', String(params.page));
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.http.request({
      method: 'GET',
      path: `/mail/sender-memory${qs ? `?${qs}` : ''}`,
      responseSchema: senderMemoryListPageSchema,
      signal,
    });
  }

  deleteSenderMemoryEntry(id: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({
      method: 'DELETE',
      path: `/mail/sender-memory/${encodeURIComponent(id)}`,
      expectEmpty: true,
      signal,
    });
  }

  listTagRules(tagId?: string, signal?: AbortSignal): Promise<MailTagRule[]> {
    const qs = tagId ? `?tagId=${encodeURIComponent(tagId)}` : '';
    return this.http.request({ path: `/mail/tag-rules${qs}`, responseSchema: z.array(mailTagRuleSchema), signal });
  }

  createTagRule(input: CreateMailTagRule, signal?: AbortSignal): Promise<MailTagRule> {
    return this.http.request({ method: 'POST', path: '/mail/tag-rules', body: input, responseSchema: mailTagRuleSchema, signal });
  }

  deleteTagRule(ruleId: string, signal?: AbortSignal): Promise<void> {
    return this.http.request({ method: 'DELETE', path: `/mail/tag-rules/${encodeURIComponent(ruleId)}`, expectEmpty: true, signal });
  }
}
